import * as CompressorJS from 'compressorjs';

import {StoredFile} from '@global-data';

const Compressor = CompressorJS.default;

/**
 * Merges two base64 images into one.
 *
 * @param {string} base64Image1 - The first base64 image string.
 * @param {string} base64Image2 - The second base64 image string.
 * @returns {Promise<string>} - A promise that resolves with the merged base64 image string.
 */
export function mergeBase64Images(base64Image1: string, base64Image2: string): Promise<string> {
	return new Promise<string>((resolve, reject) => {
		const canvas = document.createElement('canvas');
		const ctx = canvas.getContext('2d');

		const img1 = new Image();
		const img2 = new Image();

		const imgLoad = (img: HTMLImageElement, src: string) => {
			return new Promise<HTMLImageElement>((resolve, reject) => {
				img.onload = () => {
					resolve(img);
				};
				img.src = src;
			});
		};

		Promise.all([imgLoad(img1, 'data:image/png;base64,' + base64Image1), imgLoad(img2, 'data:image/png;base64,' + base64Image2)]).then(([img1, img2]) => {
			const reduceSize = 0.25; // Add this line to reduce the size of the images

			const aspectRatio1 = img1.width / img1.height;
			const aspectRatio2 = img2.width / img2.height;

			const height = Math.max(img1.height, img2.height) * reduceSize;
			const newWidth1 = height * aspectRatio1;
			const newWidth2 = height * aspectRatio2;

			canvas.width = newWidth1 + newWidth2;
			canvas.height = height;

			ctx.drawImage(img1, 0, 0, newWidth1, height);
			ctx.drawImage(img2, newWidth1, 0, newWidth2, height);

			resolve(canvas.toDataURL('image/png', 0.1)); // Further reduce the quality to make the image size smaller
		});
	});
}

/* -------------------------------------------------------------------------------------------------------------- */
/**
 * Converts a base64 image string to an HTMLImageElement.
 * @param base64Image
 * @param format
 */
export function asHTMLImageElement(base64Image: string, format: 'png' | 'jpg' | 'svg' = 'png'): HTMLImageElement {
	const img = new Image();
	if (base64Image.startsWith('data:image')) img.src = base64Image;
	else img.src = `data:image/${format};base64,${base64Image}`;
	return img;
}

/* -------------------------------------------------------------------------------------------------------------- */

export async function compressImage<ReturnType = typeof StoredFile | typeof File>(imageFile: File, options: Compressor.Options, returnType: ReturnType): Promise<ReturnType> {
	return new Promise<ReturnType>((resolve, reject) => {
		new Compressor(imageFile, {
			...options,
			async success(result: File) {
				switch (returnType) {
					case StoredFile:
						debugger;
						const base64String = await fileToBase64(result);
						const storedFile = new StoredFile(base64String, 'pnj', result.name);
						resolve(storedFile as ReturnType);
						break;
					case File:
						resolve(result as ReturnType);
						break;
					default:
						resolve(result as ReturnType);
				}
			},
			error(err) {
				reject(err);
			},
		});
	});
}

function fileToBase64(file: File): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result as string);
		reader.onerror = reject;
		reader.readAsDataURL(file);
	});
}
